import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import icons from "./data/icons";
import { getBlogById, getBlogs } from "./api/blogApi";
import { getFooter } from "./api/footerService";
import { getContact } from "./api/contactService";
import EmailLink from "./EmailLink";
import { getHome } from "./api/homeService";
// =========================================================
// FORMAT DATE + TIME
// =========================================================

const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;

    const now = new Date();

    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Future dates
    if (diffMs < 0) {
        const futureMinutes = Math.floor(Math.abs(diffMs) / (1000 * 60));

        if (futureMinutes < 1) return "Just now";
        if (futureMinutes < 60) return `in ${futureMinutes}m`;

        const futureHours = Math.floor(futureMinutes / 60);
        if (futureHours < 24) return `in ${futureHours}h`;

        const formattedDate = date.toLocaleDateString("en-IN", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
        });

        return `${formattedDate}, ${date.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
        })}`;
    }

    // Relative time
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "1d ago";
    if (diffDays <= 7) return `${diffDays}d ago`;

    // After 7 days → full date + time
    const formattedDate = `${date.toLocaleDateString("en-IN", {
        weekday: "short",
    })}, ${date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
    })} ${date.getFullYear()}`;

    return `${formattedDate}, ${date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
    })}`;
};

// =========================================================
// NORMALIZE BLOG HTML
// ---------------------------------------------------------
// Ensures images that Quill places inside a paragraph
// alongside text are moved out into their own block-level
// <p> AFTER the paragraph. Preserves text-align (left /
// center / right / justify) selected in the editor.
// =========================================================

const normalizeBlogHtml = (html) => {
    if (!html || typeof html !== "string") return "";

    if (
        typeof window === "undefined" ||
        typeof DOMParser === "undefined"
    ) {
        return html;
    }

    try {
        const parser = new DOMParser();

        const doc = parser.parseFromString(
            `<div id="__blog-root__">${html}</div>`,
            "text/html"
        );

        const root = doc.getElementById("__blog-root__");
        if (!root) return html;

        const isWhitespaceText = (node) =>
            node.nodeType === 3 &&
            node.textContent.trim().length === 0;

        const isBr = (node) =>
            node.nodeType === 1 &&
            node.tagName.toLowerCase() === "br";

        const isImageNode = (node) => {
            if (node.nodeType !== 1) return false;

            const tag = node.tagName.toLowerCase();

            if (tag === "img") return true;

            if (tag === "a") {
                const children = Array.from(node.childNodes);

                return (
                    children.length > 0 &&
                    children.every(
                        (c) =>
                            (c.nodeType === 1 &&
                                c.tagName.toLowerCase() === "img") ||
                            isWhitespaceText(c)
                    )
                );
            }

            return false;
        };

        const paragraphs = Array.from(
            root.querySelectorAll("p")
        );

        paragraphs.forEach((p) => {
            const childNodes = Array.from(p.childNodes);

            const hasImage = childNodes.some(isImageNode);
            if (!hasImage) return;

            const hasRealContent = childNodes.some(
                (n) =>
                    !isWhitespaceText(n) &&
                    !isImageNode(n) &&
                    !isBr(n)
            );

            if (!hasRealContent) return;

            const pStyle = p.getAttribute("style") || "";
            const pAlign = (
                p.getAttribute("align") || ""
            ).toLowerCase();

            const alignMatch = pStyle.match(
                /text-align\s*:\s*(left|center|right|justify)/i
            );

            const alignValue = alignMatch
                ? alignMatch[1].toLowerCase()
                : ["left", "center", "right", "justify"].includes(
                    pAlign
                )
                    ? pAlign
                    : "";

            const resultNodes = [];
            let textBuffer = [];

            const flushText = () => {
                if (textBuffer.length === 0) return;

                while (
                    textBuffer.length &&
                    isWhitespaceText(textBuffer[0])
                ) {
                    textBuffer.shift();
                }

                while (
                    textBuffer.length &&
                    isWhitespaceText(
                        textBuffer[textBuffer.length - 1]
                    )
                ) {
                    textBuffer.pop();
                }

                while (
                    textBuffer.length &&
                    isBr(textBuffer[0])
                ) {
                    textBuffer.shift();
                }

                while (
                    textBuffer.length &&
                    isBr(textBuffer[textBuffer.length - 1])
                ) {
                    textBuffer.pop();
                }

                if (textBuffer.length === 0) {
                    textBuffer = [];
                    return;
                }

                const textP = doc.createElement("p");

                if (pStyle) {
                    textP.setAttribute("style", pStyle);
                }

                if (pAlign) {
                    textP.setAttribute("align", pAlign);
                }

                textBuffer.forEach((n) =>
                    textP.appendChild(n.cloneNode(true))
                );

                resultNodes.push(textP);
                textBuffer = [];
            };

            childNodes.forEach((node) => {
                if (isWhitespaceText(node)) return;

                if (isBr(node)) {
                    if (textBuffer.length > 0) {
                        textBuffer.push(node);
                    }
                    return;
                }

                if (isImageNode(node)) {
                    flushText();

                    const imgP = doc.createElement("p");

                    if (alignValue) {
                        imgP.setAttribute(
                            "style",
                            `text-align: ${alignValue};`
                        );
                    }

                    imgP.appendChild(node.cloneNode(true));
                    resultNodes.push(imgP);
                } else {
                    textBuffer.push(node);
                }
            });

            flushText();

            if (resultNodes.length === 0) return;

            const fragment = doc.createDocumentFragment();

            resultNodes.forEach((n) =>
                fragment.appendChild(n)
            );

            p.parentNode.replaceChild(fragment, p);
        });

        return root.innerHTML;
    } catch (err) {
        console.error(
            "Failed to normalize blog HTML:",
            err
        );
        return html;
    }
};

// =========================================================
// READING PROGRESS BAR
// =========================================================

function useReadingProgress() {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;

            const docHeight =
                document.documentElement.scrollHeight -
                window.innerHeight;

            const pct =
                docHeight > 0
                    ? (scrollTop / docHeight) * 100
                    : 0;

            setProgress(
                Math.min(100, Math.max(0, pct))
            );
        };

        window.addEventListener("scroll", handleScroll, {
            passive: true,
        });

        handleScroll();

        return () => {
            window.removeEventListener(
                "scroll",
                handleScroll
            );
        };
    }, []);

    return progress;
}

// =========================================================
// BLOG DETAILS
// =========================================================
function Footer() {
    const scrollToTop = () =>
        window.scrollTo({ top: 0, behavior: "smooth" });

    const [footer, setFooter] = useState(null);
    const [contactData, setContactData] = useState(null);
    const [home, setHome] = useState(null);

    useEffect(() => {
        const loadHome = async () => {
            try {
                const res = await getHome();
                const raw = res?.data?.data ?? res?.data ?? null;
                const homeData = Array.isArray(raw) ? raw[0] : raw;
                setHome(homeData || null);
            } catch (error) {
                console.error("Failed to load home data:", error);
            }
        };

        loadHome();
    }, []);

    useEffect(() => {
        const loadContact = async () => {
            try {
                const response = await getContact();

                const contact = response?.data?.data;

                setContactData(
                    Array.isArray(contact) ? contact[0] : contact
                );
            } catch (error) {
                console.error("Failed to load contact data:", error);
            }
        };

        loadContact();
    }, []);

    useEffect(() => {
        const loadFooter = async () => {
            try {
                const res = await getFooter();
                const raw = res?.data?.data ?? [];
                const footerData = Array.isArray(raw) ? raw[0] : raw;
                setFooter(footerData || null);
            } catch (error) {
                console.error("Failed to load footer:", error);
            }
        };

        loadFooter();
    }, []);

    return (
        <footer className="w-full mt-8 border-t border-slate-700/60">

            <div className="max-w-8xl mx-auto px-8 sm:px-10 lg:px-16 py-6">

                {/* TOP FOOTER CONTENT */}
                <div
                    className="flex flex-col sm:flex-row sm:items-start justify-between gap-8 pb-8 border-b border-slate-700/60"
                >
                    <div className="flex flex-col items-start">

                        {/* FOOTER NAME */}
                        <a
                            href="/"
                            aria-label="Home"
                            className="text-lg sm:text-xl font-bold tracking-wide"
                        >
                            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                                {footer?.footername}
                            </span>
                        </a>

                        {/* DESCRIPTION */}
                        <p className="mt-1.5 max-w-xs text-slate-400 text-xs leading-snug">
                            {footer?.footerdes}
                        </p>

                        {/* SOCIAL ICONS */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">

                            {footer?.socialLinks
                                ?.filter((link) => link.showIcon)
                                .map((link) => {
                                    const Icon = icons[link.icon];

                                    if (!Icon) return null;

                                    return (
                                        <a
                                            key={link.id || link.href}
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={link.text || link.title}
                                            className="
                        relative group
                        flex items-center justify-center
                        w-9 h-9
                        rounded-lg
                        bg-slate-800/70
                        border border-transparent
                        transition-all duration-300
                        hover:-translate-y-1
                        hover:bg-slate-800
                      "
                                            style={{
                                                "--icon-color":
                                                    link.iconColor || "#94a3b8",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor =
                                                    link.iconColor || "#94a3b8";

                                                e.currentTarget.style.boxShadow =
                                                    `0 0 16px ${link.iconColor || "#94a3b8"
                                                    }`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor =
                                                    "transparent";

                                                e.currentTarget.style.boxShadow =
                                                    "none";
                                            }}
                                        >
                                            <Icon
                                                className="
                          w-4 h-4
                          transition-all duration-300
                          group-hover:scale-125
                        "
                                                style={{
                                                    color:
                                                        link.iconColor || "#94a3b8",
                                                }}
                                            />

                                            {/* TOOLTIP */}
                                            <span
                                                className="
                          absolute
                          bottom-full
                          left-1/2
                          -translate-x-1/2
                          mb-2
                          w-fit
                          max-w-[220px]
                          px-3
                          py-1.5
                          rounded-lg
                          text-white
                          text-xs
                          font-semibold
                          whitespace-nowrap
                          leading-none
                          shadow-lg
                          opacity-0
                          translate-y-1
                          group-hover:opacity-100
                          group-hover:translate-y-0
                          transition-all
                          duration-200
                          ease-out
                          pointer-events-none
                          z-50
                        "
                                                style={{
                                                    backgroundColor:
                                                        link.iconColor || "#94a3b8",
                                                }}
                                            >
                                                {link.text ||
                                                    link.title ||
                                                    link.name}

                                                <span
                                                    className="
                            absolute
                            top-full
                            left-1/2
                            -translate-x-1/2
                            w-0
                            h-0
                            border-l-[6px]
                            border-r-[6px]
                            border-t-[6px]
                            border-l-transparent
                            border-r-transparent
                          "
                                                    style={{
                                                        borderTopColor:
                                                            link.iconColor ||
                                                            "#94a3b8",
                                                    }}
                                                />
                                            </span>
                                        </a>
                                    );
                                })}

                            {/* EMAIL */}
                            <EmailLink
                                email={contactData?.email}
                                icon={
                                    <icons.Mail
                                        className="
                      w-4 h-4
                      transition-all duration-300
                      group-hover:scale-125
                    "
                                    />
                                }
                                className="
                  relative group
                  flex items-center justify-center
                  w-9 h-9
                  rounded-lg
                  bg-slate-800/70
                  text-slate-400
                  border border-transparent
                  transition-all duration-300
                  hover:-translate-y-1
                  hover:bg-slate-800
                  hover:text-cyan-300
                "
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor =
                                        "#67e8f9";

                                    e.currentTarget.style.boxShadow =
                                        "0 0 16px #67e8f9";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor =
                                        "transparent";

                                    e.currentTarget.style.boxShadow =
                                        "none";
                                }}
                            >
                                {/* EMAIL TOOLTIP */}
                                <span
                                    className="
                    absolute
                    bottom-full
                    left-1/2
                    -translate-x-1/2
                    mb-2
                    inline-block
                    w-fit
                    max-w-[220px]
                    px-3
                    py-1.5
                    rounded-lg
                    text-white
                    text-xs
                    font-semibold
                    whitespace-nowrap
                    leading-none
                    shadow-lg
                    opacity-0
                    translate-y-1
                    group-hover:opacity-100
                    group-hover:translate-y-0
                    transition-all
                    duration-200
                    ease-out
                    pointer-events-none
                    z-50
                  "
                                    style={{
                                        backgroundColor: "#37faf4",
                                        color: "#000000",
                                    }}
                                >
                                    Email

                                    <span
                                        className="
                      absolute
                      top-full
                      left-1/2
                      -translate-x-1/2
                      w-0
                      h-0
                      border-l-[6px]
                      border-r-[6px]
                      border-t-[6px]
                      border-l-transparent
                      border-r-transparent
                    "
                                        style={{
                                            borderTopColor: "#37faf4",
                                        }}
                                    />
                                </span>
                            </EmailLink>

                        </div>
                    </div>
                </div>

                {/* BOTTOM FOOTER */}
                <div
                    className="
            pt-6
            sm:pt-4
            mt-6
            sm:mt-4
            pb-4
            md:pb-0
            flex
            flex-col
            md:flex-row
            items-center
            justify-between
            gap-2
            md:gap-1.5
            text-xs
          "
                >

                    {/* COPYRIGHT */}
                    <p className="text-slate-500 text-center md:text-left leading-snug">
                        © {new Date().getFullYear()} {footer?.copyright}
                    </p>

                    {/* DEVELOPER */}
                    <p className="text-slate-500 text-center flex items-center justify-center leading-snug">

                        <span>
                            {footer?.developerDetail?.trim()}
                        </span>

                        <a
                            href={footer?.footerLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center"
                        >
                            {home?.footerLogo ? (
                                <img
                                    src={home.footerLogo}
                                    alt="Developer Logo"
                                    className="h-8 md:h-10 w-auto object-contain"
                                />
                            ) : (
                                footer?.footername && (
                                    <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent font-semibold">
                                        {footer.footername}
                                    </span>
                                )
                            )}
                        </a>

                    </p>

                </div>
            </div>
        </footer>
    );
}
function BlogDetails() {
    const { id } = useParams();
    const INK = "#0C121D";
    const PAPER = "#e9edf3";
    const RULE = "#334155";
    const BRASS = "#818CF8";
    const SAGE = "#6366F1";
    const DIM = "#94A3B8";
    const progress = useReadingProgress();

    const [post, setPost] = useState(null);
    const [allPosts, setAllPosts] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // =========================================================
    // LOAD BLOG
    // =========================================================

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setLoading(true);
                setError("");

                if (!id) {
                    throw new Error("Invalid blog ID");
                }

                const response = await getBlogById(
                    encodeURIComponent(id)
                );

                const result = response.data;

                if (!result?.success) {
                    throw new Error(
                        result?.message || "Blog not found"
                    );
                }

                setPost(result.data);
            } catch (err) {
                console.error(
                    "Failed to load blog:",
                    err
                );

                setPost(null);

                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to load blog"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
    }, [id]);

    // =========================================================
    // LOAD ALL BLOGS
    // Used for recent posts
    // =========================================================

    useEffect(() => {
        const fetchAllBlogs = async () => {
            try {
                const response = await getBlogs();

                const result = response.data;

                if (result?.success) {
                    setAllPosts(result.data || []);
                }
            } catch (err) {
                console.error(
                    "Failed to load recent blogs:",
                    err
                );
            }
        };

        fetchAllBlogs();
    }, []);

    // =========================================================
    // SCROLL TO TOP WHEN BLOG CHANGES
    // =========================================================

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    // =========================================================
    // NORMALIZED BLOG CONTENT
    // ---------------------------------------------------------
    // Splits paragraphs that contain BOTH text and images
    // so the image becomes its own block-level <p> after the
    // paragraph. Preserves text-align saved by the editor.
    // =========================================================

    const normalizedContent = useMemo(() => {
        if (!post?.content) return "";
        return normalizeBlogHtml(post.content);
    }, [post?.content]);

    // =========================================================
    // RELATED POSTS — SAME CATEGORY, MAX 8
    // =========================================================

    const relatedPosts = useMemo(() => {
        if (!allPosts.length || !post) {
            return [];
        }

        const currentCategory = String(post.category || "")
            .trim()
            .toLowerCase();

        if (!currentCategory) {
            return [];
        }

        return [...allPosts]
            .filter((item) => {
                if (item.id === post.id) return false;

                const itemCategory = String(item.category || "")
                    .trim()
                    .toLowerCase();

                return itemCategory === currentCategory;
            })
            .sort(
                (a, b) =>
                    new Date(b.date || 0).getTime() -
                    new Date(a.date || 0).getTime()
            )
            .slice(0, 8);
    }, [allPosts, post]);

    // =========================================================
    // MORE BLOGS — EXCLUDE CURRENT + RELATED POSTS
    // =========================================================

    const moreBlogsPool = useMemo(() => {
        if (!allPosts.length || !post) {
            return [];
        }

        return [...allPosts]
            .filter((item) => item.id !== post.id)
            .sort(
                (a, b) =>
                    new Date(b.date || 0).getTime() -
                    new Date(a.date || 0).getTime()
            );
    }, [allPosts, post]);

    const moreBlogs = moreBlogsPool.slice(0, 4);

    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {
        return (
            <section
                className="
          min-h-screen
          bg-[#0C121D]
          flex
          items-center
          justify-center
          px-6
        "
            >
                <div className="min-h-screen flex items-center justify-center bg-[#0C121D]">
                    <div className="relative h-14 w-14 animate-spin rounded-full border-[6px] border-slate-700 border-t-indigo-500" />
                </div>
            </section>
        );
    }

    // =========================================================
    // ERROR / NOT FOUND
    // =========================================================

    if (!post) {
        return (
            <section
                className="
          min-h-screen
          bg-[#0C121D]
          flex
          items-center
          justify-center
          px-6
        "
            >
                <div className="text-center">

                    <h1
                        className="
              text-4xl
              font-bold
              text-white
              mb-4
            "
                    >
                        Blog Not Found
                    </h1>

                    <p className="text-slate-400 mb-8">
                        {error ||
                            "The blog you are looking for does not exist."}
                    </p>

                    <Link
                        to="/blog"
                        className="
              inline-flex
              px-6
              py-3
              rounded-xl
              font-semibold
              bg-gradient-to-r
              from-indigo-600
              to-purple-600
              text-white
              hover:scale-105
              transition-all
            "
                    >
                        Back to Blog
                    </Link>

                </div>
            </section>
        );
    }

    // =========================================================
    // BLOG PAGE — NEW MAGAZINE STYLE
    // =========================================================

    return (
        <section
            className="min-h-screen text-slate-100 overflow-hidden"
            style={{
                background:
                    "radial-gradient(circle at 15% 0%, rgba(99,102,241,0.14), transparent 28%), radial-gradient(circle at 90% 18%, rgba(34,211,238,0.08), transparent 24%), #07111f",
            }}
        >
            {/* READING PROGRESS */}
            <div className="fixed top-0 left-0 right-0 h-[3px] z-[100] bg-slate-900/70">
                <div
                    className="h-full bg-gradient-to-r from-cyan-400 via-indigo-500 to-violet-500 transition-[width] duration-150"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* TOP NAV */}
            <div className="sticky top-0 z-50 border-b border-white/[0.07] bg-[#07111f]/80 backdrop-blur-xl">
                <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link
                        to="/blog"
                        className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-cyan-400/50 bg-cyan-400/10 text-sm text-cyan-300 hover:bg-cyan-400/20 hover:border-cyan-400 hover:text-white transition-all duration-300"
                    >
                        <span className="group-hover:-translate-x-1 transition-transform">
                            ←
                        </span>
                        <span>Back</span>
                    </Link>

                    <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                        Reading
                    </div>
                </div>
            </div>

            {/* HERO */}
            <main className="max-w-8xl mx-auto px-3 sm:px-5 lg:px-6 py-8 sm:py-10 lg:py-12">

                <div
                    className={
                        relatedPosts.length > 0
                            ? "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_350px] xl:grid-cols-[minmax(0,1fr)_400px] gap-5 xl:gap-6 items-start"
                            : "block"
                    }
                >

                    {/* =========================================================
            MAIN CONTENT
            HERO + ARTICLE ARE INSIDE ONE SINGLE BOX
        ========================================================= */}

                    <div className="min-w-0">

                        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-slate-900/50 shadow-2xl shadow-black/30">

                            {/* ================= HERO ================= */}

                            <header>
                                <div className="relative min-h-[360px] sm:min-h-[400px] lg:min-h-[500px] overflow-hidden rounded-[22px]">

                                    {/* ================= FULL HERO IMAGE ================= */}

                                    {post.coverImage ? (
                                        <img
                                            src={post.coverImage}
                                            alt={post.title}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]"
                                        />
                                    ) : (
                                        <div
                                            className={`absolute inset-0 ${post.gradient ||
                                                "bg-gradient-to-br from-indigo-600 via-violet-600 to-cyan-500"
                                                }`}
                                        />
                                    )}


                                    {/* ================= IMAGE OVERLAY ================= */}

                                    {/* Overall dark overlay */}
                                    <div className="absolute inset-0 bg-black/20" />

                                    {/* Bottom gradient for text readability */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 to-transparent" />

                                    {/* Slight left/right gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />
                                    {/* ================= HERO CONTENT ================= */}

                                    <div className="absolute inset-x-0 bottom-0 z-10">

                                        <div className="p-5 sm:p-7 lg:p-10 xl:p-12">

                                            {/* CATEGORY + DATE + AUTHOR */}

                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4">

                                                {/* CATEGORY */}

                                                {post.categoryLabel && (
                                                    <span className="inline-flex items-center rounded-md bg-blue-500 px-3 py-1.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.12em] text-white">
                                                        {post.categoryLabel}
                                                    </span>
                                                )}


                                                {/* DATE */}

                                                <span className="font-mono text-[10px] sm:text-xs text-white/85">
                                                    • {formatDate(post.date)}
                                                </span>


                                                {/* AUTHOR */}

                                                {post.authorName && (
                                                    <span className="font-mono text-[10px] sm:text-xs text-white/85">
                                                        • {post.authorName}
                                                    </span>
                                                )}

                                            </div>


                                            {/* ================= TITLE ================= */}

                                            <div className="flex flex-col gap-4 w-full">

                                                <div className="min-w-0 w-full max-w-[1400px]">

                                                    <h1
                                                        className="
        text-xl
        sm:text-2xl
        md:text-3xl
        lg:text-[2rem]
        xl:text-[2.25rem]
        font-bold
        leading-[1.12]
        tracking-[-0.02em]
        text-white
        break-normal
        whitespace-normal
    "
                                                    >
                                                        {post.title}
                                                    </h1>

                                                    {post.excerpt && (
                                                        <p className="mt-4 max-w-4xl text-xs sm:text-sm lg:text-base leading-6 text-white/75 line-clamp-2">
                                                            {post.excerpt}
                                                        </p>
                                                    )}

                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                </div>
                            </header>


                            {/* ================= DIVIDER ================= */}

                            <div className="mx-5 sm:mx-7 lg:mx-9">
                                <div className="h-px bg-white/[0.08]" />
                            </div>


                            {/* ================= ARTICLE ================= */}

                            <article className="px-5 py-7 sm:px-7 sm:py-8 lg:px-9 lg:py-9 w-full min-w-0">
                                <div
                                    className="blog-content w-full max-w-full min-w-0"
                                    dangerouslySetInnerHTML={{
                                        __html: normalizedContent,
                                    }}
                                />

                            </article>


                            {/* ================= ARTICLE END ================= */}

                            <div className="flex items-center justify-center gap-3 py-10">

                                <span className="h-px w-16 bg-gradient-to-r from-transparent to-indigo-500/50" />

                                <span className="text-xs tracking-[5px] text-indigo-400">
                                    ✦ ✦ ✦
                                </span>

                                <span className="h-px w-16 bg-gradient-to-l from-transparent to-indigo-500/50" />

                            </div>

                        </div>

                    </div>


                    {/* =========================================================
            RELATED POSTS
            COMPLETELY OUTSIDE THE MAIN BOX
        ========================================================= */}

                    {relatedPosts.length > 0 && (
                        <aside className="hidden lg:block sticky top-24">

                            <div className="rounded-[18px] border border-white/[0.08] bg-slate-900/55 backdrop-blur-xl overflow-hidden shadow-xl shadow-black/20">

                                {/* RELATED HEADER */}

                                <div className="p-4 border-b border-white/[0.07]">

                                    <div>

                                        <p className="text-[9px] uppercase tracking-[0.28em] text-cyan-400 font-semibold">
                                            You may also like
                                        </p>

                                        <h2 className="mt-1 text-lg font-semibold text-white uppercase">
                                            Related stories
                                        </h2>

                                    </div>

                                </div>


                                {/* RELATED LIST */}

                                <div className="p-1.5">

                                    {relatedPosts.map((rp, index) => (
                                        <Link
                                            key={rp.id}
                                            to={
                                                rp.encryptedId
                                                    ? `/blog/${encodeURIComponent(rp.encryptedId)}`
                                                    : "#"
                                            }
                                            className="group flex flex-row-reverse gap-2.5 p-2.5 rounded-lg hover:bg-white/[0.045] transition-all"
                                        >

                                            {/* IMAGE — RIGHT SIDE */}
                                            <div className="relative w-[68px] h-[56px] shrink-0 overflow-hidden rounded-lg border border-white/10 bg-slate-800">
                                                {rp.coverImage ? (
                                                    <img
                                                        src={rp.coverImage}
                                                        alt={rp.title}
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-indigo-600/50 to-cyan-500/10" />
                                                )}
                                            </div>

                                            {/* CONTENT — LEFT SIDE */}
                                            <div className="min-w-0 flex-1">

                                                {/* CATEGORY */}
                                                {rp.categoryLabel && (
                                                    <span className="text-[9px] uppercase tracking-[0.12em] text-slate-500">
                                                        {rp.categoryLabel}
                                                    </span>
                                                )}

                                                {/* TITLE */}
                                                <h3 className="text-[12px] font-semibold leading-[1.3] text-slate-200 line-clamp-2 group-hover:text-cyan-300 transition-colors">
                                                    {rp.title}
                                                </h3>

                                                {/* DESCRIPTION */}
                                                {rp.excerpt && (
                                                    <p className="mt-1 text-[10px] leading-[1.4] text-slate-500 line-clamp-2">
                                                        {rp.excerpt}
                                                    </p>
                                                )}

                                                {/* DATE */}
                                                <p className="font-mono mt-1.5 text-[9px] text-slate-500">
                                                    {formatDate(rp.date)}
                                                </p>

                                            </div>

                                        </Link>
                                    ))}

                                </div>

                            </div>

                        </aside>
                    )}

                </div>


                {/* =========================================================
        MOBILE RELATED POSTS
        OUTSIDE MAIN BOX
    ========================================================= */}

                {relatedPosts.length > 0 && (
                    <section className="lg:hidden mt-5">

                        <div className="rounded-[18px] border border-white/[0.08] bg-slate-900/55 backdrop-blur-xl overflow-hidden shadow-xl shadow-black/20">

                            {/* HEADER */}

                            <div className="p-4 sm:p-5 border-b border-white/[0.07]">

                                <p className="text-[9px] uppercase tracking-[0.28em] text-cyan-400 font-semibold">
                                    You may also like
                                </p>

                                <h2 className="mt-1 text-lg sm:text-xl uppercase font-semibold text-white">
                                    Related stories
                                </h2>

                            </div>


                            {/* RELATED POSTS */}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 p-2">

                                {relatedPosts.map((rp, index) => (

                                    <Link
                                        key={rp.id}
                                        to={
                                            rp.encryptedId
                                                ? `/blog/${encodeURIComponent(rp.encryptedId)}`
                                                : "#"
                                        }
                                        className="group flex gap-3 p-3 rounded-xl hover:bg-white/[0.045] transition-all"
                                    >

                                        {/* IMAGE */}

                                        <div className="relative w-[82px] h-[64px] sm:w-[92px] sm:h-[68px] shrink-0 overflow-hidden rounded-lg border border-white/10 bg-slate-800">

                                            {rp.coverImage ? (
                                                <img
                                                    src={rp.coverImage}
                                                    alt={rp.title}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-indigo-600/50 to-cyan-500/10" />
                                            )}
                                        </div>


                                        {/* CONTENT */}

                                        <div className="min-w-0 flex-1">

                                            {/* CATEGORY */}

                                            {rp.categoryLabel && (
                                                <span className="text-[9px] uppercase tracking-[0.12em] text-slate-500">
                                                    {rp.categoryLabel}
                                                </span>
                                            )}


                                            {/* TITLE */}

                                            <h3 className="text-xs sm:text-sm font-semibold leading-[1.3] text-slate-200 line-clamp-2 group-hover:text-cyan-300 transition-colors">
                                                {rp.title}
                                            </h3>


                                            {/* DESCRIPTION / EXCERPT */}

                                            {rp.excerpt && (
                                                <p className="mt-1 text-[10px] sm:text-[11px] leading-[1.4] text-slate-500 line-clamp-2">
                                                    {rp.excerpt}
                                                </p>
                                            )}


                                            {/* DATE */}

                                            <p className="font-mono mt-1.5 text-[9px] text-slate-500">
                                                {formatDate(rp.date)}
                                            </p>

                                        </div>

                                    </Link>

                                ))}

                            </div>

                        </div>

                    </section>
                )}

            </main>

            {/* MORE BLOGS */}
            {moreBlogs.length > 0 && (
                <section className="hidden lg:block border-t border-white/[0.07] bg-black/10">
                    <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                        <div className="flex items-end justify-between gap-4 mb-7">
                            <div>
                                <p className="text-[9px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">
                                    Keep exploring
                                </p>
                                <h2 className="mt-1.5 text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                    More Field Notes
                                </h2>
                            </div>

                            {moreBlogsPool.length > 4 && (
                                <Link
                                    to="/blog"
                                    className="shrink-0 inline-flex items-center gap-2 rounded-full border border-indigo-400/30 bg-indigo-500/10 px-4 py-2 text-xs font-medium text-indigo-300 hover:bg-indigo-500/20 hover:border-indigo-400/50 transition-all"
                                >
                                    More<span>→</span>
                                </Link>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {moreBlogs.map((rp) => (
                                <Link
                                    key={rp.id}
                                    to={rp.encryptedId ? `/blog/${encodeURIComponent(rp.encryptedId)}` : "#"}
                                    className="group overflow-hidden rounded-2xl border border-white/[0.08] bg-slate-900/45 hover:-translate-y-1 hover:border-cyan-400/25 transition-all duration-300"
                                >
                                    <div className="relative h-40 overflow-hidden bg-slate-800">
                                        {rp.coverImage ? (
                                            <img
                                                src={rp.coverImage}
                                                alt={rp.title}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-indigo-600/40 via-violet-500/20 to-cyan-500/10" />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />
                                    </div>

                                    <div className="p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            {rp.category && (
                                                <span className="text-[9px] hidden uppercase tracking-[0.16em] font-semibold text-cyan-400">
                                                    {rp.category}
                                                </span>
                                            )}
                                            {rp.categoryLabel && (
                                                <span className="text-[9px] uppercase tracking-[0.12em] text-slate-500">
                                                    {rp.categoryLabel}
                                                </span>
                                            )}
                                        </div>

                                        <h3 className="text-sm font-semibold leading-snug line-clamp-2 text-slate-200 group-hover:text-cyan-300 transition-colors">
                                            {rp.title}
                                        </h3>

                                        <div className="mt-4 pt-3 font-mono border-t border-white/[0.07] flex items-center justify-between text-[10px] text-slate-500">
                                            <span>{formatDate(rp.date)}</span>
                                            <span className="text-indigo-300 group-hover:translate-x-1 transition-transform">→</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <style>{`
    /* =========================================================
       BLOG ARTICLE CONTENT — COMPACT PROFESSIONAL TYPOGRAPHY
    ========================================================= */

    .blog-content {
        color: #ffffff;
        font-size: 0.92rem;
        line-height: 1.7;
        letter-spacing: 0.004em;
        font-weight: 400;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
        max-width: 100%;
        width: 100%;
        min-width: 0;
        font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    .blog-content * {
        max-width: 100%;
    }

    /* ================= PARAGRAPHS ================= */

    .blog-content p {
        margin: 0 0 0.95rem;
        color: #ffffff;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
        font-weight: 400;
        letter-spacing: 0.004em;
    }

    .blog-content p:last-child {
        margin-bottom: 0;
    }

    .blog-content > p:first-of-type {
        font-size: 0.98rem;
        line-height: 1.72;
        color: #f1f5f9;
    }

    /* ================= HEADINGS ================= */

    .blog-content h1,
    .blog-content h2,
    .blog-content h3,
    .blog-content h4,
    .blog-content h5,
    .blog-content h6 {
        color: #ffffff;
        font-weight: 700;
        letter-spacing: -0.018em;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
        scroll-margin-top: 6rem;
    }

    .blog-content h1 {
        font-size: 1.55rem;
        line-height: 1.25;
        margin: 1.6rem 0 0.75rem;
        padding-bottom: 0.45rem;
        border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }

    .blog-content h2 {
        font-size: 1.22rem;
        line-height: 1.35;
        margin: 1.55rem 0 0.7rem;
        padding-left: 0.75rem;
        position: relative;
    }

    .blog-content h2::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0.15rem;
        bottom: 0.15rem;
        width: 3px;
        border-radius: 3px;
        background: linear-gradient(180deg, #22d3ee 0%, #6366f1 100%);
    }

    .blog-content h3 {
        font-size: 1.05rem;
        line-height: 1.4;
        margin: 1.35rem 0 0.6rem;
        color: #e0e7ff;
    }

    .blog-content h4 {
        font-size: 0.96rem;
        line-height: 1.45;
        margin: 1.2rem 0 0.5rem;
        color: #e0e7ff;
    }

    .blog-content h5 {
        font-size: 0.9rem;
        line-height: 1.5;
        margin: 1.05rem 0 0.45rem;
        color: #e2e8f0;
    }

    .blog-content h6 {
        font-size: 0.82rem;
        line-height: 1.5;
        margin: 0.95rem 0 0.4rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #a5b4fc;
    }

    /* ================= TEXT ================= */

    .blog-content strong,
    .blog-content b {
        color: #ffffff;
        font-weight: 700;
    }

    .blog-content em,
    .blog-content i {
        font-style: italic;
        color: #f1f5f9;
    }

    .blog-content u {
        text-decoration: underline;
        text-decoration-color: rgba(129, 140, 248, 0.6);
        text-underline-offset: 3px;
    }

    .blog-content mark {
        background: linear-gradient(180deg, transparent 60%, rgba(99, 102, 241, 0.35) 60%);
        color: #ffffff;
        padding: 0 0.15em;
    }

    /* ================= LISTS ================= */

    .blog-content ul {
        list-style: none;
        padding-left: 1.4rem;
        margin: 0.7rem 0 1rem;
    }

    .blog-content ul li {
        position: relative;
        padding-left: 0.4rem;
        margin: 0.35rem 0;
        color: #ffffff;
    }

    .blog-content ul li::before {
        content: "";
        position: absolute;
        left: -1rem;
        top: 0.62em;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: linear-gradient(135deg, #22d3ee, #6366f1);
        box-shadow: 0 0 6px rgba(99, 102, 241, 0.5);
    }

    .blog-content ol {
        list-style: decimal;
        padding-left: 1.4rem;
        margin: 0.7rem 0 1rem;
    }

    .blog-content ol li {
        margin: 0.35rem 0;
        padding-left: 0.3rem;
        color: #ffffff;
    }

    .blog-content ol li::marker {
        color: #818cf8;
        font-weight: 700;
    }

    .blog-content ul ul,
    .blog-content ol ol,
    .blog-content ul ol,
    .blog-content ol ul {
        margin-top: 0.25rem;
        margin-bottom: 0.25rem;
    }

    /* ================= BLOCKQUOTE ================= */

    .blog-content blockquote {
        position: relative;
        margin: 1.15rem 0;
        padding: 0.8rem 1rem 0.8rem 1.15rem;
        border-left: 3px solid #6366f1;
        border-radius: 0 10px 10px 0;
        background: linear-gradient(90deg, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0.02) 100%);
        color: #e2e8f0;
        font-style: italic;
        font-size: 0.92rem;
        line-height: 1.65;
    }

    .blog-content blockquote p {
        color: #e2e8f0;
        margin: 0 0 0.4rem;
    }

    .blog-content blockquote p:last-child {
        margin-bottom: 0;
    }

    /* ================= INLINE CODE ================= */

    .blog-content code {
        background: rgba(30, 41, 59, 0.95);
        color: #67e8f9;
        padding: 0.14rem 0.38rem;
        border-radius: 0.3rem;
        font-size: 0.86em;
        font-family: 'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace;
        border: 1px solid rgba(148, 163, 184, 0.15);
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
    }

    /* ================= CODE BLOCK ================= */

    .blog-content pre {
        background: #0b1220;
        border: 1px solid rgba(99, 102, 241, 0.22);
        border-radius: 0.7rem;
        padding: 0.85rem 1rem;
        margin: 1.1rem 0;
        overflow-x: auto;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: 'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace;
        font-size: 0.82rem;
        line-height: 1.6;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03), 0 8px 24px -8px rgba(0, 0, 0, 0.5);
    }

    .blog-content pre code {
        background: transparent;
        padding: 0;
        color: #a5f3fc;
        border: none;
        white-space: pre-wrap;
        word-break: break-word;
        font-size: inherit;
    }

    /* ================= LINKS ================= */

    .blog-content a {
        color: #a5b4fc;
        text-decoration: underline;
        text-decoration-color: rgba(165, 180, 252, 0.4);
        text-underline-offset: 3px;
        transition: color 0.2s ease, text-decoration-color 0.2s ease;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
    }

    .blog-content a:hover {
        color: #67e8f9;
        text-decoration-color: #67e8f9;
    }

    /* ================= HORIZONTAL LINE ================= */

    .blog-content hr {
        border: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.45), transparent);
        margin: 1.6rem 0;
    }

    /* =========================================================
       ARTICLE IMAGES — ORIGINAL LARGE PRESENTATION
       (NOT reduced)
    ========================================================= */

    .blog-content img {
        display: block;
        width: auto;
        max-width: 100%;
        max-height: 520px;
        height: auto;
        object-fit: contain;
        border-radius: 14px;
        border: 1px solid rgba(148, 163, 184, 0.14);
        margin-top: 1.6rem;
        margin-bottom: 1.6rem;
        margin-left: 0;
        margin-right: auto;
        box-shadow: 0 20px 40px -18px rgba(0, 0, 0, 0.65), 0 4px 14px -6px rgba(0, 0, 0, 0.4);
        background: rgba(15, 23, 42, 0.6);
        transition: transform 0.4s ease, box-shadow 0.4s ease;
    }

    .blog-content img:hover {
        transform: translateY(-2px);
        box-shadow: 0 28px 55px -20px rgba(0, 0, 0, 0.75), 0 6px 18px -6px rgba(99, 102, 241, 0.25);
    }

    /* =========================================================
       IMAGE ALIGNMENT — DRIVEN BY THE PARENT PARAGRAPH
    ========================================================= */

    .blog-content p[style*="text-align:center"] img,
    .blog-content p[style*="text-align: center"] img,
    .blog-content p[align="center"] img {
        margin-left: auto;
        margin-right: auto;
    }

    .blog-content p[style*="text-align:right"] img,
    .blog-content p[style*="text-align: right"] img,
    .blog-content p[align="right"] img {
        margin-left: auto;
        margin-right: 0;
    }

    .blog-content p[style*="text-align:left"] img,
    .blog-content p[style*="text-align: left"] img,
    .blog-content p[align="left"] img {
        margin-left: 0;
        margin-right: auto;
    }

    /* ================= IMAGE WRAPPERS ================= */

    .blog-content p img {
        vertical-align: middle;
        max-width: 88%;
    }

    /* ================= HTML ALIGNMENT ================= */

    .blog-content [style*="text-align: center"] {
        text-align: center;
    }

    .blog-content [style*="text-align: right"] {
        text-align: right;
    }

    .blog-content [style*="text-align: left"] {
        text-align: left;
    }

    /* =========================================================
       PROFESSIONAL TABLE
    ========================================================= */

    .blog-content table {
        width: 100%;
        max-width: 100%;
        border-collapse: collapse !important;
        border-spacing: 0 !important;
        table-layout: auto !important;
        margin: 1.2rem 0;
        background: #06163f !important;
        border: 1px solid #aebbd3 !important;
        border-radius: 8px;
        font-size: 0.86rem;
        line-height: 1.5;
        overflow: hidden;
    }

    .blog-content table th,
    .blog-content table td {
        border: 1px solid #aebbd3 !important;
        padding: 9px 12px !important;
        background: #06163f !important;
        color: #ffffff !important;
        text-align: left;
        vertical-align: middle;
        font-size: 0.86rem;
        line-height: 1.5;
        min-width: 0 !important;
        min-height: 0 !important;
        height: auto !important;
        box-sizing: border-box;
        box-shadow: none !important;
        outline: none !important;
    }

    .blog-content table thead th {
        background: #0a1f52 !important;
        color: #ffffff !important;
        font-weight: 700 !important;
        font-size: 0.86rem;
        letter-spacing: 0.01em;
        white-space: normal;
    }

    .blog-content table tbody td {
        background: #06163f !important;
        color: #ffffff !important;
        font-weight: 400 !important;
    }

    .blog-content table tr {
        background: #06163f !important;
    }

    .blog-content table tr:hover,
    .blog-content table tr:hover th,
    .blog-content table tr:hover td,
    .blog-content table th:hover,
    .blog-content table td:hover {
        background: #06163f !important;
        color: #ffffff !important;
    }

    .blog-content table .ql-cell-focused,
    .blog-content table td.ql-cell-focused,
    .blog-content table th.ql-cell-focused {
        background: #06163f !important;
        color: #ffffff !important;
        border: 1px solid #aebbd3 !important;
        box-shadow: none !important;
        outline: none !important;
    }

    .blog-content table .ql-cell-focused .ql-table-block,
    .blog-content table td.ql-cell-focused .ql-table-block,
    .blog-content table th.ql-cell-focused .ql-table-block {
        background: transparent !important;
        color: #ffffff !important;
        box-shadow: none !important;
        outline: none !important;
        margin: 0 !important;
    }

    .blog-content table .ql-cell-focused::before,
    .blog-content table .ql-cell-focused::after,
    .blog-content table td.ql-cell-focused::before,
    .blog-content table td.ql-cell-focused::after,
    .blog-content table th.ql-cell-focused::before,
    .blog-content table th.ql-cell-focused::after {
        background: transparent !important;
        box-shadow: none !important;
        outline: none !important;
        border: none !important;
    }

    .blog-content table th:last-child,
    .blog-content table td:last-child {
        background: #06163f !important;
        color: #ffffff !important;
        border-color: #aebbd3 !important;
        box-shadow: none !important;
        outline: none !important;
    }

    .blog-content table p {
        margin: 0 !important;
        padding: 0 !important;
        color: inherit !important;
        line-height: 1.5;
    }

    .blog-content table strong,
    .blog-content table b {
        color: #ffffff !important;
        font-weight: 700 !important;
    }

    /* =========================================================
       FIGURE + CAPTION
    ========================================================= */

    .blog-content figure {
        margin: 1.2rem 0;
    }

    .blog-content figcaption {
        color: #94a3b8;
        font-size: 0.76rem;
        line-height: 1.5;
        margin-top: 0.5rem;
        text-align: center;
        font-style: italic;
        letter-spacing: 0.01em;
    }

    /* =========================================================
       MOBILE
    ========================================================= */

    @media (max-width: 640px) {

        .blog-content {
            font-size: 0.88rem;
            line-height: 1.65;
        }

        .blog-content > p:first-of-type {
            font-size: 0.92rem;
        }

        .blog-content p {
            margin-bottom: 0.85rem;
        }

        /* ================= MOBILE HEADINGS ================= */

        .blog-content h1 {
            font-size: 1.35rem;
            margin: 1.35rem 0 0.6rem;
        }

        .blog-content h2 {
            font-size: 1.1rem;
            margin: 1.3rem 0 0.6rem;
            padding-left: 0.65rem;
        }

        .blog-content h2::before {
            width: 3px;
        }

        .blog-content h3 {
            font-size: 0.98rem;
        }

        .blog-content h4 {
            font-size: 0.9rem;
        }

        .blog-content h5 {
            font-size: 0.85rem;
        }

        .blog-content h6 {
            font-size: 0.78rem;
        }

        /* ================= MOBILE IMAGES — ORIGINAL LARGE ================= */

        .blog-content img,
        .blog-content p img {
            max-width: 100%;
            max-height: 420px;
            margin-top: 1.2rem;
            margin-bottom: 1.2rem;
            border-radius: 12px;
        }

        /* ================= MOBILE BLOCKQUOTE ================= */

        .blog-content blockquote {
            padding: 0.7rem 0.85rem 0.7rem 1rem;
            font-size: 0.86rem;
            margin: 1rem 0;
        }

        /* =====================================================
           MOBILE TABLE
        ===================================================== */

        .blog-content table {
            display: block;
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            font-size: 0.8rem;
            border: 1px solid #aebbd3 !important;
        }

        .blog-content table th,
        .blog-content table td {
            padding: 8px 10px !important;
            min-width: 110px !important;
            font-size: 0.8rem;
            border: 1px solid #aebbd3 !important;
            background: #06163f !important;
            color: #ffffff !important;
        }

        .blog-content table thead th {
            background: #0a1f52 !important;
            color: #ffffff !important;
            font-size: 0.8rem;
        }

        .blog-content table .ql-cell-focused,
        .blog-content table td.ql-cell-focused,
        .blog-content table th.ql-cell-focused {
            background: #06163f !important;
            color: #ffffff !important;
            border-color: #aebbd3 !important;
            box-shadow: none !important;
            outline: none !important;
        }
    }

`}</style>

            <Footer />

        </section>
    );
}

export default BlogDetails;